import { useState, useEffect, Fragment } from 'react';

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
} from '@mui/material';

import { SelectChangeEvent } from '@mui/material/Select';

import AnalyzerSelectLocal from '../analyzers/AnalyzerSelectLocal';
import SwitchSelectLocal from 'features/switches/SwitchSelectLocal';
import InfoPopup from 'components/InfoPopup';

import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useSwitches } from 'features/switches/api/getSwitches';
import { useGetGroupedAnalyzersWithSwitch } from 'features/switches/api/getGroupedAnalyzerWithSwitch';
import { FormSwitch } from './types';
import { AnalyzerSwitch } from 'types';
import { AnalyzerSwitchOperation } from 'features/switches/api/executeAnalyzerSwitchOperations ';
import { executeAnalyzerSwitchOperations } from 'features/switches/api/executeAnalyzerSwitchOperations ';

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedAnalyzerId: number;
}

const GroupInstrumentsDialog = ({
  open,
  handleClose,
  selectedAnalyzerId,
}: Props) => {
  const { data: analyzers } = useAnalyzers({});
  const { data: switches } = useSwitches({});
  const { data: selectedAnalyzer } = useAnalyzer({
    analyzerId: selectedAnalyzerId,
  });
  const { data: groupedAnalyzersWithSwitch, refetch } =
    useGetGroupedAnalyzersWithSwitch({
      analyzerId: selectedAnalyzerId,
    });

  const generateInitialMap = () => {
    const initialMap = new Map();
    const placeholderFormSwitch: FormSwitch = {
      row_id: undefined,
      switch_id: -1,
      switch_channel: undefined,
      switch_output: undefined,
    };
    initialMap.set(-1, [placeholderFormSwitch]);
    return initialMap;
  };

  const [formSwitchesMap, setFormSwitchesMap] =
    useState<Map<number, FormSwitch[]>>(generateInitialMap);

  const [switchPortsMap, setSwitchPortsMap] = useState<Map<number, number>>(
    new Map()
  );

  const [initTrigger, setInitTrigger] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleCloseAndReset = () => {
    setErrorMessage(null);
    handleClose();
    refetch();
    setInitTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    //Handles intiilzaition of the switch port map - used to determine how many ports each switch has.
    const newSwitchPortsMap = new Map<number, number>();
    switches?.forEach((switchItem) => {
      newSwitchPortsMap.set(switchItem.id, switchItem?.number_of_outputs || -1);
    });
    setSwitchPortsMap(newSwitchPortsMap);
    //Handles initialization of the switches and their connections. FormSwitchesMap contains all of this data.
    if (groupedAnalyzersWithSwitch && groupedAnalyzersWithSwitch.length > 0) {
      const newFormSwitchesMap = new Map<number, FormSwitch[]>();
      groupedAnalyzersWithSwitch.forEach((item: any) => {
        const formSwitch: FormSwitch = {
          row_id: item.id,
          switch_id: item.switch_id,
          switch_channel: item.switch_channel,
          switch_output: item.switch_output,
        };

        const existingSwitches = newFormSwitchesMap.get(item.switch_id) || [];
        newFormSwitchesMap.set(item.switch_id, [...existingSwitches, formSwitch]);
      });
      setFormSwitchesMap(newFormSwitchesMap);
    } else {
      setFormSwitchesMap(generateInitialMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedAnalyzersWithSwitch, selectedAnalyzerId, initTrigger]);

  const handleSwitchChanged = (e: SelectChangeEvent, switchId: number) => {
    const newSwitchId = Number(e.target.value);
    const selectedSwitch = switches?.find((sw) => sw.id === newSwitchId);
    const newNumberOfPorts = selectedSwitch?.number_of_outputs || 1;

    setFormSwitchesMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      const updatedFormSwitches = Array.from(
        { length: newNumberOfPorts },
        () => ({
          row_id: undefined,
          switch_id: newSwitchId,
          switch_channel: undefined,
          switch_output: undefined,
        })
      );
      updatedMap.delete(switchId);

      updatedMap.set(newSwitchId, updatedFormSwitches);
      return updatedMap;
    });
  };

  const handleAnalyzerPortChanged = (
    e: any,
    switchId: number,
    index: number
  ) => {
    const value = Number(e.target.value);

    setFormSwitchesMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      const formSwitches = updatedMap.get(switchId) || [];
      const updatedFormSwitches = [...formSwitches];
      const formSwitch = { ...updatedFormSwitches[index] };
      if (formSwitch) {
        formSwitch.switch_channel = value;
        updatedFormSwitches[index] = formSwitch;
        updatedMap.set(switchId, updatedFormSwitches);
      }
      return updatedMap;
    });
  };

  const handleSwitchOutputChanged = (
    e: any,
    switchId: number,
    index: number
  ) => {
    const valueStr = e.target.value;
    const maxPorts = switchPortsMap.get(switchId) || 1;

    let valueNum: any;
    if (valueStr === '') {
      valueNum = ''; // Allow empty field
    } else {
      valueNum = Number(valueStr);
      if (valueNum < 1 || valueNum > maxPorts) {
        valueNum = Math.min(Math.max(valueNum, 1), maxPorts); // Clamp value to range [1, maxPorts]
      }
    }

    setFormSwitchesMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      const formSwitches = updatedMap.get(switchId) || [];
      const formSwitch = formSwitches[index];
      if (formSwitch) {
        formSwitch.switch_output = valueNum;
      }
      updatedMap.set(switchId, formSwitches);
      return updatedMap;
    });
  };

  const handleAddSwitch = () => {
    setFormSwitchesMap((prevMap) => {
      const newSwitchId = -1;
      const newNumberOfPorts = 0;

      const newMap = new Map(prevMap);
      const newFormSwitches = Array.from({ length: newNumberOfPorts }, () => ({
        row_id: undefined,
        switch_id: newSwitchId,
        switch_channel: undefined,
        switch_output: undefined,
      }));
      newMap.set(newSwitchId, newFormSwitches);
      return newMap;
    });
  };

  const handleSubmit = async () => {
    // Fetch old connections
    const oldAnalyzerSwitchPairs = groupedAnalyzersWithSwitch;

    // Convert the current connections to a similar format for comparison
    const currentConnections: AnalyzerSwitch[] = [];
    for (const [switchId, formSwitches] of formSwitchesMap.entries()) {
      for (const formSwitch of formSwitches) {
        if (
          formSwitch &&
          formSwitch.switch_channel &&
          formSwitch.switch_output
        ) {
          currentConnections.push({
            id: formSwitch.row_id || -1,
            switch_channel: formSwitch.switch_channel,
            analyzer_id: selectedAnalyzer?.id || -1,
            switch_id: switchId,
            switch_output: formSwitch.switch_output,
          });
        }
      }
    }
    if (!oldAnalyzerSwitchPairs) {
      return;
    }

    const operations: AnalyzerSwitchOperation[] = [];

    // Identify Connections to Update
    for (const oldConnection of oldAnalyzerSwitchPairs) {
      const newConnection = currentConnections.find(
        (c) => c.id === oldConnection.id
      );
      if (newConnection) {
        if (
          newConnection.switch_channel !== oldConnection.switch_channel ||
          newConnection.switch_output !== oldConnection.switch_output
        ) {
          operations.push({
            kind: 'Update',
            analyzerId: selectedAnalyzerId,
            switchId: oldConnection.switch_id,
            id: oldConnection.id,
            newSwitchChannel: newConnection.switch_channel,
            newSwitchOutput: newConnection.switch_output,
          });
        }
      } else {
        // Connection is in old, but not in current (Remove)
        operations.push({
          kind: 'Remove',
          analyzerId: selectedAnalyzerId,
          switchId: oldConnection.switch_id,
          id: oldConnection.id,
        });
      }
    }

    // Identify Connections to Add
    for (const currentConnection of currentConnections) {
      if (!oldAnalyzerSwitchPairs.some((o: { id: number; }) => o.id === currentConnection.id)) {
        operations.push({
          kind: 'Add',
          analyzerId: selectedAnalyzerId,
          switchId: currentConnection.switch_id,
          newSwitchChannel: currentConnection.switch_channel,
          newSwitchOutput: currentConnection.switch_output,
        });
      }
    }

    if (operations.length > 0) {
      try {
        await executeAnalyzerSwitchOperations(operations);
        handleCloseAndReset();
      } catch (error) {
        console.error('Failed to execute operations:', error);
        setErrorMessage('Duplicate Port Detected for Switch or Analyzer');
      }
    } else {
      setErrorMessage('No operations to execute.');
    }
  };

  const handleDeleteSwitch = (groupIndex: number) => {
    const entriesArray = Array.from(formSwitchesMap.entries());

    const [switchIdToDelete] = entriesArray[groupIndex];

    setFormSwitchesMap((prevMap) => {
      const updatedMap = new Map(prevMap);
      updatedMap.delete(switchIdToDelete);
      return updatedMap;
    });
  };

  const addedSwitchIds = new Set(formSwitchesMap.keys());

  return (
    <Dialog open={open} onClose={handleCloseAndReset} maxWidth='xs' fullWidth>
      <DialogTitle>Group Instrument</DialogTitle>
      <DialogContent>
        <Grid container justifyContent='center' spacing={2}>
          <Grid item xs={12} />
          <Grid item xs={12}>
            <AnalyzerSelectLocal
              analyzers={analyzers}
              handleAnalyzerChanged={() => {}}
              selectedAnalyzerId={selectedAnalyzerId}
              fullWidth
              disabled
            />
          </Grid>
          {Array.from(formSwitchesMap.entries()).map(
            ([switchId, formSwitches], groupIndex) => (
              <Fragment key={groupIndex}>
                <Grid item container alignItems='center' spacing={1} xs={12}>
                  <Grid item xs>
                    <SwitchSelectLocal
                      switches={switches?.filter(
                        (sw) => !addedSwitchIds.has(sw.id) || sw.id === switchId
                      )}
                      selectedSwitchId={switchId}
                      handleSwitchChanged={(e) =>
                        handleSwitchChanged(e, switchId)
                      }
                      fullWidth
                    />
                  </Grid>
                  {groupIndex > 0 && (
                    <Grid item>
                      <Button
                        variant='contained'
                        color='error'
                        onClick={() => handleDeleteSwitch(groupIndex)}
                      >
                        DELETE
                      </Button>
                    </Grid>
                  )}
                </Grid>
                {Array.from(
                  { length: switchPortsMap.get(switchId) || 0 },
                  (_, index) => (
                    <Grid
                      key={index}
                      item
                      container
                      alignItems='center'
                      spacing={1}
                      xs={12}
                    >
                      <Grid item xs={6}>
                        <TextField
                          variant='outlined'
                          required
                          fullWidth
                          id='switch_channel'
                          label='Analyzer Port'
                          name='switch_channel'
                          size='small'
                          type='number'
                          inputProps={{ min: '1', max: '16', step: '1' }}
                          value={formSwitches[index]?.switch_channel || ''}
                          onChange={(e) =>
                            handleAnalyzerPortChanged(e, switchId, index)
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          variant='outlined'
                          required
                          fullWidth
                          id='switch_output'
                          label='Switch Output'
                          name='switch_output'
                          size='small'
                          type='number'
                          inputProps={{
                            min: '1',
                            max: switchPortsMap.get(switchId) || '1',
                            step: '1',
                          }}
                          value={formSwitches[index]?.switch_output || ''}
                          onChange={(e) =>
                            handleSwitchOutputChanged(e, switchId, index)
                          }
                        />
                      </Grid>
                    </Grid>
                  )
                )}
              </Fragment>
            )
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAddSwitch} variant='contained' color='primary'>
          Add Another Switch
        </Button>
        <Button
          onClick={handleCloseAndReset}
          variant='contained'
          color='secondary'
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant='contained' color='primary'>
          Submit
        </Button>
      </DialogActions>
      <InfoPopup
        openCondition={!!errorMessage}
        title='Error'
        message={errorMessage || ''}
        error
      />
    </Dialog>
  );
};

export default GroupInstrumentsDialog;
